<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('cashCollection.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('cashCollection.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('cashCollection.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('cashCollection.date')
                      }}</label>
                      <a-range-picker
                        style="width: 100%"
                        v-model="model.date_range"
                        format="DD-MM-YYYY"
                        :allowClear="false"
                        valueFormat="DD-MM-YYYY"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('incomeBySale.branch') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        mode="multiple"
                        placeholder="All"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.branch_id"
                        :options="branchList"
                        @change="onChangeBranch"
                      ></a-select>
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label>{{ $t('stockInDetail.location') }}</label>

                        <a-select
                          v-model="model.location_id"
                          mode="multiple"
                          :defaultActiveFirstOption="true"
                          showSearch
                          :filterOption="
                            (input, option) =>
                              option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                          "
                          :options="locations"
                          :placeholder="$t('all')"
                          class="ts-w-full"
                        ></a-select>
                        <div class="validate" v-if="errors.has('location_id')">
                          {{ errors.first('location_id') }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('cashCollection.saleType') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        v-model="model.pos_type"
                        mode="multiple"
                        :options="posType"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('type')">
                        {{ errors.first('type') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('cashCollection.cashier') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.cashier_id"
                        :options="cashiers"
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                      ></a-select>
                      <div class="validate" v-if="errors.has('sale_id')">
                        {{ errors.first('sale_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('cashCollection.customerName') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.customer_id"
                        :options="customerLists"
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                        @search="handleSearchCustomer"
                        :loading="customer_loading"
                      ></a-select>
                      <div class="validate" v-if="errors.has('customer_id')">
                        {{ errors.first('customer_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>{{ $t('cashCollection.paymentMethod') }}</label>
                      <a-select
                        style="width: 100%; font-size: 15px"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.payment_methods_id"
                        :options="PaymentMethods"
                        mode="multiple"
                        :maxTagCount="1"
                        :placeholder="$t('all')"
                      ></a-select>
                      <div class="validate" v-if="errors.has('sale_id')">
                        {{ errors.first('sale_id') }}
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        outline
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                        ><i class="fas fa-file-excel"></i>
                        {{ $t('excel') }}</ts-button
                      >
                    </div>
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        color="primary"
                        @click.prevent="preview"
                        :waiting="waiting"
                        ><i class="fas fa-file-pdf text-red"></i>
                        {{ $t('preview') }}</ts-button
                      >
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('cashCollection.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim, debounce } from 'lodash'

export default {
  name: 'cashCollection',
  data() {
    return {
      loading: false,
      customer_loading: false,
      waiting: false,
      exporting: false,
      src: null,
      errors: new Errors(),
      PaymentMethods: [],
      cashiers: [],
      customers: [],
      branchList: [],
      locations: [],
      model: {
        sale_id: [],
        cashier_id: [],
        customer_id: [],
        payment_methods_id: [],
        branch_id: undefined,
        location_id: undefined,
        pos_type: undefined,
        date_range: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY'),
        ],
      },
    }
  },
  computed: {
    customerLists() {
      return this.customers.map((el) => ({
        value: el.customer_id,
        label: el.customer_name,
      }))
    },
    posType() {
      return [
        { value: 'Retail', label: 'Retail Sale' },
        { value: 'WholeSale', label: 'Whole Sale' },
      ]
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    ...mapActions('report/finance', ['cashCollectionByCashier']),
    handleSearchCustomer: debounce(async function (value) {
      if (!value) {
        return false
      } else {
        this.customer_loading = true
        await this.$store
          .dispatch('report/resource/getCustomer', {
            searchCustomerName: value,
          })
          .then((response) => {
            this.customers = response.data
          })
          .catch((error) => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.customer_loading = false
          })
      }
    }, 500),
    async fetchData() {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then((response) => {
          this.branchList = response.data.map((el) => ({
            value: el.branch_id,
            label: el.branch_name_en,
          }))
        })

      await this.$store
        .dispatch('report/resource/getCashier')
        .then((response) => {
          this.cashiers = response.data.map((el) => ({
            value: el.user_id,
            label: el.name,
          }))
        })
      await this.$store
        .dispatch('report/resource/getCustomer')
        .then((response) => {
          this.customers = response.data
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })

      await this.$store
        .dispatch('report/resource/getPaymentMethod')
        .then((response) => {
          this.PaymentMethods = response.data.map((el) => ({
            value: el.payment_method_id,
            label: el.payment_method,
          }))
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onChangeBranch() {
      this.locationLoading = true
      this.locations = []
      this.model.location_id = undefined
      this.$store
        .dispatch('report/resource/getLocation', {
          branch_id: this.model.branch_id,
        })
        .then((response) => {
          this.locations = response.data.map((el) => ({
            value: el.location_id,
            label: el.location_name_en,
          }))
        })
        .finally(() => {
          this.locationLoading = false
        })
    },
    preview() {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.cashCollectionByCashier(this.model)
        .then((response) => {
          this.src = response.url
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/finance/cashCollectionByCashierExcel', this.model)
        .then((response) => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch((err) => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
  },
}
</script>
